import { Component, OnInit, Input, EventEmitter, Output, ElementRef, ViewChild, signal, OnChanges, computed, effect } from '@angular/core';
import { FlowObjectInstance, FlowObjectInstanceState, FlowObjectDefinition, FlowObjectType } from '../../../models/flow-object.model';
import { Papel } from '../../../models/edocs.model';
import { FlowDefinition, FlowTarget } from '../../../models/flow.model';
import { FORMIO_OPTIONS } from './formio/formio-options';
import { AuthService } from '../../../services/auth.service';
import { EDocsService } from '../../../services/edocs.service';
import { ToastrService } from 'ngx-toastr';
import { Enums } from '../../../shared/enums';
import { NgxSpinnerService } from 'ngx-spinner';
import { Utils } from '../../../shared/utils';
import { InputDataTaskForm } from '../../../models/input-output-data.model';
import { MatDialog } from '@angular/material/dialog';
import { PdfPreviewDialogComponent } from '../../pdf-preview-dialog/pdf-preview-dialog.component';
import { FlowObjectDefinitionService } from '../../../services/flow-object-definition.service';
import { ConfirmationService } from 'primeng/api';
import { OnDestroy } from '@angular/core';
import { compressToUTF16, decompressFromUTF16 } from 'lz-string';
import { ConfigSchemaFlowDefinition } from '../../../models/config-schema.model';
import { IMPORTS } from './_imports';

@Component({
    selector: 'fob-form',
    templateUrl: './fob-form.component.html',
    styleUrl: './fob-form.component.scss',
    standalone: true,
    imports: IMPORTS
})
export class FlowObjectFormComponent implements OnInit, OnChanges, OnDestroy {
    // #region [ViewChild]
    @ViewChild('papelRef') papelRef: ElementRef;
    // #endregion

    // #region [Type props]
    FlowObjectType = FlowObjectType;
    // #endregion

    // #region [props]
    flowDefinitionConfigSchema: ConfigSchemaFlowDefinition = null;
    formioOptions: any = FORMIO_OPTIONS;
    filesUploadedCounter = 0;
    totalFilesCounter: number = null;
    isFirstFormChange = true;
    // #endregion

    // #region [signals]
    $flowDefinition = signal(null as FlowDefinition);
    $papeisSelector = signal([] as Papel[]);
    $model = signal(null as FlowObjectDefinition);
    $flowObjectInstance = signal(null as FlowObjectInstance);
    $formSchema = signal(null as any);
    $inputData = signal(new InputDataTaskForm());
    $isReadOnly = signal(false);
    $isLocalLoading = signal(false);
    $isBootstrapFinished = signal(false);
    // #endregion

    // #region [computeds]
    $currentUser = computed(() => this.authService.$user());
    $shouldShowPapelSelector = computed(() => this.$flowDefinition()?.targetId != FlowTarget.Citizen && this.$papeisSelector().length > 0);
    $hasDraft = computed(() =>
        Utils.isNotNullOrEmpty(window.localStorage.getItem(this.$flowDefinition().id))
        && window.localStorage.getItem(`${this.$flowDefinition().id}_default`) != window.localStorage.getItem(this.$flowDefinition().id)
    );
    // #endregion

    // #region [effects]
    $currentUserCpfEffect = effect(() => window['_$_accpf'] = this.$currentUser()?.cpf);
    // #endregion

    // #region [Input/Output]
    @Input() _flowObjectDefinition: FlowObjectDefinition;
    @Input() _flowObjectInstance: FlowObjectInstance;
    @Input() _flowDefinition: FlowDefinition;
    @Output() _submitEvent = new EventEmitter<InputDataTaskForm>();
    // #endregion

    constructor(
        private dialog: MatDialog,
        private toastr: ToastrService,
        private spinner: NgxSpinnerService,
        private confirmationService: ConfirmationService,
        private authService: AuthService,
        private eDocsService: EDocsService,
        private flowObjectDefinitionService: FlowObjectDefinitionService
    ) {
        // #region [hook do Formio para interceptação do submit e eventual alteração dos dados submetidos]
        // utilizado aqui para o tratamento diferenciado necessário aos
        // componentes customizados de arquivo(i.e.POST no Minio), dentre outros
        this.formioOptions.hooks.beforeSubmit = async (submission, next) => {
            this.spinner.show('genericFullscreen');

            try {
                let keys = Object.keys(submission.data);
                let values = Object.values(submission.data);
                let components = JSON.parse(this._flowObjectDefinition.formSchema).components;

                // #region [tratamento de componentes do tipo "GeoJSON"]
                let geoJsonComponents = components.filter(x => x.type == 'geoJson');
                for (let x of geoJsonComponents) {
                    let value = submission.data[x.key];
                    if (Utils.isNullOrEmpty(value)) continue;

                    let normalizedValue = JSON.stringify(JSON.parse(value));
                    submission.data[x.key] = normalizedValue;
                }
                // #endregion

                // #region [tratamento de Datagrids]
                let datagridComponents = components.filter(x => ['datagrid', 'datagridCustom'].includes(x.type));

                for (let x of datagridComponents) {
                    let value = submission.data[x.key];
                    if (value == null) continue;

                    submission.data['___datagridsColumnOrders'] = submission.data['___datagridsColumnOrders'] || [];

                    let columnOrder = [];
                    if (!submission.data['___datagridsColumnOrders'].some(y => y.key == x.key)) {
                        columnOrder = x.components.map((y, z) => ({ key: y.key, index: z }));
                        columnOrder = columnOrder.filter(x => new Set(value.flatMap(y => Object.keys(y))).has(x.key));
                        submission.data['___datagridsColumnOrders'].push({
                            key: x.key,
                            value: columnOrder
                        });
                    }

                    // tratamento de campos condicionais em Datagrids
                    for (let item of value) {
                        columnOrder.filter(x => !Object.keys(item).includes(x.key)).forEach(x => {
                            item[x.key] = '_+-*~$§•§$~*-+_';
                        });
                    }

                    // #region [tratamento de componentes do tipo "Lista Filtrada" dentro de Datagrids]
                    let selectFilteredComponents = x.components.filter(y => y.type == 'selectFiltered');
                    for (let y of selectFilteredComponents) {
                        for (let z of value) {
                            if (z[y.key] == null) continue;
                            z[y.key] = z[y.key].value;
                        }
                    }
                    // #endregion

                    // #region [tratamento de componentes do tipo "GeoJSON" dentro de Datagrids]
                    let geoJsonComponents = x.components.filter(y => y.type == 'geoJson');
                    for (let y of geoJsonComponents) {
                        for (let z of value) {
                            if (Utils.isNullOrEmpty(z[y.key])) continue;
                            let normalizedValue = JSON.stringify(JSON.parse(z[y.key]));
                            z[y.key] = normalizedValue;
                        }
                    }
                    // #endregion
                }
                // #endregion

                // #region [tratamento de componentes do tipo "Valor Monetário": valores com decimais completos e cifras]
                let currencyComponents = components.filter(x => x.type == 'currency');

                for (let x of currencyComponents) {
                    if (x.currency == null) continue;

                    let formatter = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: x.currency });
                    let i = keys.indexOf(x.key);

                    if (i == -1) {
                        submission.data[x.key] = formatter.format(0);
                    } else {
                        let value = values[i] as number;
                        submission.data[x.key] = formatter.format(value);
                    }
                }
                // #endregion

                // #region [tratamento específico para componentes com máscara múltipla (e.g. "Telefone")]
                for (let i = 0; i < values.length; i++) {
                    // considerando componentes na raiz do formulário
                    if (
                        typeof values[i] == 'object'
                        && !Array.isArray(values[i])
                        && values[i]?.hasOwnProperty('value')
                    ) {
                        submission.data[keys[i]] = values[i]['value'];
                    }
                    // considerando componentes dentro de tabelas
                    else if (
                        typeof values[i] == 'object'
                        && Array.isArray(values[i])
                    ) {
                        for (let j = 0; j < values[i]['length']; j++) {
                            let subkeys = Object.keys(submission.data[keys[i]][j]);
                            let subvalues = Object.values(submission.data[keys[i]][j]);

                            for (let k = 0; k < subkeys.length; k++) {
                                if (
                                    typeof subvalues[k] == 'object'
                                    && !Array.isArray(subvalues[k])
                                    && subvalues[k]?.hasOwnProperty('value')
                                ) {
                                    submission.data[keys[i]][j][subkeys[k]] = subvalues[k]['value'];
                                }
                            }
                        }
                    }
                }
                // #endregion

                // #region [tratamento prévio de labels de componentes do tipo "Arquivo(s) PDF"]
                let singles = Utils.filterDeep(components, x => x.type == 'pdfUpload').map(x => x.key);
                for (let key of singles) {
                    if (submission.data[key] != null) {
                        submission.data[key].label = [Utils.findDeep(components, x => x.key == key).label];
                    }
                }

                let multiples = Utils.filterDeep(components, x => x.type == 'pdfUploadMultiple').map(x => x.key);
                for (let key of multiples) {
                    if (submission.data[key] != null) {
                        submission.data[key].label = [];

                        for (let i = 1; i <= submission.data[key].fileName.length; i++) {
                            submission.data[key].label.push(`${Utils.findDeep(components, x => x.key == key).label} (${i})`);
                        }
                    }
                }
                // #endregion

                // #region [preview do documento final]
                const response = await this.flowObjectDefinitionService.previewFinalPdf({
                    id: this.$model().id,
                    unitId: this.$flowDefinition().unitId,
                    signerId: this.$inputData().eDocsData.signerId,
                    formData: submission.data
                });

                if (!response.isSuccess) {
                    this.toastr.error(response.message.description, Enums.Messages.Error, Utils.getToastrErrorOptions());
                    return;
                }
                // #endregion

                setTimeout(() => {
                    this.confirmationService.confirm({
                        message: Enums.Messages.FinalPreviewDialogMessage,
                        accept: () => {
                            let dialog = this.dialog.open(PdfPreviewDialogComponent, {
                                data: {
                                    content: response.data,
                                    title: Enums.Messages.FinalPreviewDialogTitle,
                                    isFinalPreview: true
                                }
                            });

                            dialog.afterClosed().subscribe(shouldNotProceed => {
                                if (shouldNotProceed) {
                                    next([{ message: null }]);
                                    return;
                                }

                                this.confirmationService.confirm({
                                    message: Enums.Messages.ConfirmSendForm,
                                    accept: async () => {
                                        // #region [checagem de seleção de papel antes de prosseguir]
                                        if (Utils.isNullOrEmpty(this.$inputData().eDocsData.signerId)) {
                                            Utils.scrollToTop();
                                            this.toastr.error(Enums.Messages.NoSignerIdError, Enums.Messages.Error, Utils.getToastrErrorOptions());
                                            next([{ message: null }]);
                                            return;
                                        }
                                        // #endregion

                                        let components = JSON.parse(this._flowObjectDefinition.formSchema).components;

                                        // #region [tratamento de componentes do tipo "Protocolo E-Docs"]
                                        // componentes do tipo "Protocolo E-Docs" adicionados diretamente no formulário
                                        let eDocsProtocolComponents = components.filter(x => x.type == 'eDocsProtocol');

                                        let eDocsProtocolList: { key: string, id: string, protocol: string, canUse: boolean, hasErrors: boolean }[] = [];
                                        for (let component of eDocsProtocolComponents) {
                                            let key = component.key;
                                            let value = submission.data[key];

                                            let canUseResult = await Utils.canUseEDocsDocument(value, this.eDocsService, this.toastr);
                                            if (Utils.isNotNullOrEmpty(value)) {
                                                eDocsProtocolList.push({
                                                    key: key,
                                                    id: canUseResult.id,
                                                    protocol: value,
                                                    canUse: canUseResult.canUse,
                                                    hasErrors: canUseResult.hasErrors
                                                });
                                            }
                                        }

                                        if (eDocsProtocolList.length > 0) {
                                            if (eDocsProtocolList.some(x => !x.canUse)) {
                                                let printableErrorsList = eDocsProtocolList.filter(x => !x.canUse && !x.hasErrors).map(x => x.protocol);

                                                if (printableErrorsList.length > 0) {
                                                    let flattened = printableErrorsList.join('; ');
                                                    this.toastr.warning(Enums.Messages.EDocsProtocolDocumentsPendency.replace('{0}', flattened), Enums.Messages.Pendency, Utils.getToastrErrorOptions());
                                                }

                                                next([{ message: null }]);
                                                return;
                                            }

                                            for (let item of eDocsProtocolList) {
                                                if (item.canUse) {
                                                    submission.data[item.key + '_$$_guid_$$_'] = item.id;
                                                } else {
                                                    submission.data[item.key + '_$$_guid_$$_'] = null;
                                                }
                                            }
                                        }

                                        // componentes do tipo "Protocolo E-Docs" pertencentes a Tabelas de Dados (datagridCustom)
                                        eDocsProtocolList = [];
                                        let datagridCustomComponents = components.filter(x => x.type == 'datagridCustom');
                                        for (let datagridCustom of datagridCustomComponents) {
                                            let datagridCustomProtocolComponents = datagridCustom.components.filter(x => x.type == 'eDocsProtocol');

                                            for (let component of datagridCustomProtocolComponents) {
                                                if (submission.data[datagridCustom.key] == null) continue;

                                                for (let i = 0; i < submission.data[datagridCustom.key].length; i++) {
                                                    let row = submission.data[datagridCustom.key][i];
                                                    let key = `${datagridCustom.key}_${component.key}_${i}`;
                                                    let value = row[component.key];

                                                    let canUseResult = await Utils.canUseEDocsDocument(value, this.eDocsService, this.toastr);
                                                    if (Utils.isNotNullOrEmpty(value)) {
                                                        eDocsProtocolList.push({
                                                            key: key,
                                                            id: canUseResult.id,
                                                            protocol: value,
                                                            canUse: canUseResult.canUse,
                                                            hasErrors: canUseResult.hasErrors
                                                        });
                                                    }
                                                }
                                            }
                                        }

                                        // componentes do tipo "Protocolo E-Docs" pertencentes a Tabelas de Layout (tableCustom)
                                        let tableCustomAllComponents = components.filter(x => x.type == 'tableCustom').map(x => x.rows).map(x => x[0]).flat().map(x => x.components).flat();
                                        let tableCustomProtocolComponents = tableCustomAllComponents.filter(x => x.type == 'eDocsProtocol');
                                        for (let component of tableCustomProtocolComponents) {
                                            if (submission.data[component.key] == null) continue;

                                            let key = component.key;
                                            let value = submission.data[key];

                                            let canUseResult = await Utils.canUseEDocsDocument(value, this.eDocsService, this.toastr);
                                            if (Utils.isNotNullOrEmpty(value)) {
                                                eDocsProtocolList.push({
                                                    key: key,
                                                    id: canUseResult.id,
                                                    protocol: value,
                                                    canUse: canUseResult.canUse,
                                                    hasErrors: canUseResult.hasErrors
                                                });
                                            }
                                        }

                                        if (eDocsProtocolList.length > 0) {
                                            if (eDocsProtocolList.some(x => !x.canUse)) {
                                                let printableErrorsList = eDocsProtocolList.filter(x => !x.canUse && !x.hasErrors).map(x => x.protocol);

                                                if (printableErrorsList.length > 0) {
                                                    let flattened = printableErrorsList.join('; ');
                                                    this.toastr.warning(Enums.Messages.EDocsProtocolDocumentsPendency.replace('{0}', flattened), Enums.Messages.Pendency, Utils.getToastrErrorOptions());
                                                }

                                                next([{ message: null }]);
                                                return;
                                            }

                                            for (let item of eDocsProtocolList) {
                                                if (item.canUse) {
                                                    submission.data[item.key + '_$$_guid_$$_'] = item.id;
                                                } else {
                                                    submission.data[item.key + '_$$_guid_$$_'] = null;
                                                }
                                            }
                                        }
                                        // #endregion

                                        // #region [tratamento de componentes do tipo "Arquivo(s) PDF"]
                                        let submittedCustomFileComponentKeys = Object.keys(submission.data).filter(x => submission.data[x]?.isCustomFileComponent);
                                        let submittedCustomFileComponentValues = Object.values(submission.data).filter((x: any) => x?.isCustomFileComponent);

                                        // #region [checagem redundante de componentes customizados de arquivo obrigatórios]
                                        // contorno de bug do Formio que não valida mais o campo automaticamente uma vez que o mesmo seja limpo/esvaziado
                                        let requiredCustomFileComponents = Utils.filterDeep(
                                            components,
                                            x => ['pdfUpload', 'pdfUploadMultiple'].includes(x.type)
                                                && x.validate.required === true
                                                && document.querySelector(`.formio-component-${x.key}:not(.formio-hidden)`) != null
                                        ) as any[];
                                        let noncompliant = requiredCustomFileComponents.filter(x =>
                                            !submittedCustomFileComponentKeys.includes(x.key)
                                            || submission.data[x.key].fileName.length == 0
                                        );

                                        if (noncompliant.length > 0) {
                                            let componentLabels = '';
                                            noncompliant.forEach(x => componentLabels += `"${x.label}"; `);
                                            componentLabels = componentLabels.trim().split(';').slice(0, -1).join(';');

                                            this.toastr.warning(Enums.Messages.RequiredFilesPendency.replace('{0}', componentLabels), Enums.Messages.Pendency, Utils.getToastrErrorOptions());
                                            next([{ message: null }]);
                                            return;
                                        }
                                        // #endregion

                                        if (submittedCustomFileComponentKeys.length == 0) {
                                            // prossegue com o lifecycle padrão do Formio
                                            next(null, submission);
                                        } else {
                                            this.totalFilesCounter = 0;
                                            submittedCustomFileComponentValues.forEach(x => {
                                                this.totalFilesCounter += x['fileName']?.length;
                                            });

                                            for (let i = 0; i < submittedCustomFileComponentKeys.length; i++) {
                                                let item = submission.data[submittedCustomFileComponentKeys[i]];
                                                let bufferLabel = [];

                                                for (let j = 0; j < item.fileName.length; j++) {
                                                    let label = (submittedCustomFileComponentValues[i] as any).label[j];

                                                    // garante que o label do componente esteja correto
                                                    bufferLabel = (bufferLabel || []).concat([label]);

                                                    if (j == item.fileName.length - 1) {
                                                        submission.data[submittedCustomFileComponentKeys[i]].label = bufferLabel;
                                                    }

                                                    this.filesUploadedCounter++;

                                                    if (this.filesUploadedCounter == this.totalFilesCounter) {
                                                        // checa a validade da assinatura ICP-Brasil de anexos que a exijam, caso exista algum no form;
                                                        // checa se o anexo é capturável no E-Docs
                                                        if (
                                                            await Utils.isValidIcpBrasil(submission.data, this.$formSchema(), this.eDocsService, this.toastr)
                                                            && await Utils.isValidEDocsDocument(submission.data, this.$formSchema(), this.eDocsService, this.toastr)
                                                        ) {
                                                            // prossegue com o lifecycle padrão do Formio
                                                            next(null, submission);
                                                        } else {
                                                            next([{ message: null }]);
                                                        }

                                                        break;
                                                    }
                                                }
                                            }

                                            setTimeout(() => {
                                                this.filesUploadedCounter = 0;
                                                this.totalFilesCounter = null;
                                            }, 500);
                                        }
                                        // #endregion
                                    },
                                    reject: () => {
                                        next([{ message: null }]);
                                        this.confirmationService.close();
                                    }
                                });
                            });
                        },
                        reject: () => {
                            next([{ message: null }]);
                            this.confirmationService.close();
                        }
                    });
                }, 200);
            } finally {
                this.spinner.hide('genericFullscreen');
            }
        };
        // #endregion

        window.addEventListener('beforeunload', () => this.ngOnDestroy());
    }

    ngOnInit() {
        this.$model.set(this.$model() ? this.$model() : this.$flowObjectInstance().flowObjectDefinition);
        this.$formSchema.set(Utils.isNotNullOrEmpty(this.$model()?.formSchema) ? JSON.parse(this.$model().formSchema) : null);
        this.flowDefinitionConfigSchema = JSON.parse(this.$flowDefinition().configSchema);

        if (this.$formSchema() != null) {
            this.processFormSchema();
        }

        // #region [se rota "/flow-instance"]
        if (this.$flowObjectInstance() != null) {
            this.$inputData.set(JSON.parse(this.$flowObjectInstance().inputData) as InputDataTaskForm);
            this.$isReadOnly.set([FlowObjectInstanceState.Finished, FlowObjectInstanceState.NotStarted].includes(this.$flowObjectInstance().stateId));
        }
        // #endregion

        // #region [seletor de papéis]
        if (this.$flowDefinition()?.targetId != FlowTarget.Citizen) {
            if (this.$flowDefinition()?.targetId != FlowTarget.Whitelist && this.flowDefinitionConfigSchema.shouldIgnoreTargetBasedRoleFiltering) {
                this.$papeisSelector.set(this.$currentUser().papeis.filter(x =>
                    x.lotacao?.organizacao?.organizacaoPai?.guid.toLowerCase() == this.$flowDefinition().publishedPatriarchId.toLowerCase()
                ));
            } else {
                switch (this.$flowDefinition().targetId) {
                    case FlowTarget.Patriarch:
                        this.$papeisSelector.set(this.$currentUser().papeis.filter(x =>
                            x.lotacao?.organizacao?.organizacaoPai?.guid.toLowerCase() == this.$flowDefinition().publishedPatriarchId.toLowerCase()
                        ));
                        break;

                    case FlowTarget.Organization:
                        this.$papeisSelector.set(this.$currentUser().papeis.filter(x =>
                            x.lotacao?.organizacao?.guid.toLowerCase() == this.$flowDefinition().publishedOrganizationId.toLowerCase()
                        ));
                        break;

                    case FlowTarget.Unit:
                        this.$papeisSelector.set(this.$currentUser().papeis.filter(x =>
                            x.lotacaoGuid.toLowerCase() == this.$flowDefinition().publishedUnitId.toLowerCase()
                        ));
                        break;

                    case FlowTarget.Whitelist:
                        this.$papeisSelector.set(this.$currentUser().papeis.filter(x =>
                            this.$flowDefinition().flowTargetWhitelist.some(y => x.lotacaoGuid?.toLowerCase() == y.unitId?.toLowerCase())
                            || this.$flowDefinition().flowTargetWhitelist.some(y => x.lotacao?.organizacao?.guid?.toLowerCase() == y.organizationId?.toLowerCase())
                        ));
                        break;
                }
            }

            // se rota "/flow-definition"
            if (this.$flowObjectInstance() == null) {
                // força o usuário a selecionar um papel manualmente
                setTimeout(() => Utils.updSignal(this.$inputData, x => {
                    (x.eDocsData ??= {}).signerId = null;
                    return x;
                }), 50);
            }
        } else {
            Utils.updSignal(this.$inputData, x => {
                (x.eDocsData ??= {}).signerId = this.$currentUser().id;
                return x;
            });
        }
        // #endregion

        // #region [contorno para o i18n disponibilizado de maneira não ideal pelo Formio]
        let elementsObserved = [];
        setInterval(() => {
            let elements = Array.from(document.querySelectorAll('.formio-component [ref=charcount]'));
            let newElements = elements.filter(x => !elementsObserved.includes(x));
            if (newElements.length > 0) {
                newElements.forEach((x: HTMLElement) => {
                    const config = { attributeFilter: ['class'] };
                    const callback = () => x.innerText = x.innerText.replace('remaining', 'restantes');
                    const observer = new MutationObserver(callback);
                    observer.observe(x, config);
                });

                elementsObserved = elementsObserved.concat(newElements);
            }
        }, 1000);
        // #endregion

        // reinicia a fila de upload de arquivos em campos dos tipos "pdfUpload"/"pdfUploadMultiple"
        window['_$_fileQueue'] = [];

        setTimeout(() => this.$isBootstrapFinished.set(true), 100);
    }

    ngOnChanges() {
        this.$model.set(this._flowObjectDefinition);
        this.$flowObjectInstance.set(this._flowObjectInstance);
        this.$flowDefinition.set(this._flowDefinition);
    }

    ngOnDestroy() {
        delete window['_$_fileQueue'];
    }

    onSubmit(event) {
        if (window['_$_fileQueue'] != null && window['_$_fileQueue'].length > 0) {
            this.toastr.warning(Enums.Messages.UnfinishedFileUploads, Enums.Messages.Warning, Utils.getToastrErrorOptions());
            return;
        }

        if (this.$flowObjectInstance() == null || this.$flowObjectInstance().stateId == FlowObjectInstanceState.NotStarted) {
            Utils.updSignal(this.$inputData, x => {
                x.data = event.data;
                return x;
            });
            this._submitEvent.emit(this.$inputData());
        }
    }

    onFormLoad(event) {
        // #region [se rota "/flow-definition"]
        if (this.$flowObjectInstance() == null) {
            let defaultFormData = window.localStorage.getItem(`${this.$flowDefinition().id}_default`);
            let formData = window.localStorage.getItem(this.$flowDefinition().id);

            // carrega rascunho do formulário
            if (
                defaultFormData != null
                && formData != null
                && defaultFormData != formData
            ) {
                let draft = JSON.parse(decompressFromUTF16(formData));

                if (draft?.version == this.$flowDefinition().version) {
                    Utils.updSignal(this.$inputData, x => {
                        x.data = draft.data;
                        return x;
                    });
                    this.toastr.success(Enums.Messages.FormDraftRecovered, Enums.Messages.Form);
                } else {
                    window.localStorage.removeItem(this.$flowDefinition().id);
                }
            }
        }
        // #endregion

        // #region [ajuste para exibição adequada entre formatos "Fixo" e "Celular" em campos do tipo "phoneNumber"]
        if (this.$isReadOnly()) {
            setTimeout(() => {
                let datagridComponents = event.components.filter(x => ['datagrid', 'datagridCustom'].includes(x.type));
                let phoneNumberComponents = event.components.filter(x => x.type == 'phoneNumber');

                for (let datagrid of datagridComponents) {
                    phoneNumberComponents = phoneNumberComponents.concat(datagrid.components.filter(x => x.type == 'phoneNumber'));
                }

                for (let component of phoneNumberComponents) {
                    let componentInput = document.querySelector(`#${component.id} input`) as HTMLInputElement;
                    let componentSelect = document.querySelector(`#${component.id} select`) as HTMLSelectElement;

                    if (componentInput && componentSelect && componentInput.value.replace(/[^\d]/g, '').length == 10) {
                        componentSelect.value = Enums.Messages.LandlinePhoneText;
                        componentSelect.dispatchEvent(new CustomEvent('change'));
                    }
                }
            }, 500);
        }
        // #endregion

        // #region [ajuste para exibição adequada de componentes do tipo "datagrid"]
        setTimeout(() => {
            const datagridComponents = event.components.filter(x => ['datagrid', 'datagridCustom'].includes(x.type));
            if (datagridComponents.length > 0) {
                setInterval(() => {
                    for (let component of datagridComponents) {
                        let componentTable = document.querySelector(`.formio-component-${component.key} table`) as HTMLTableElement;

                        if (componentTable != null) {
                            const cols = componentTable.querySelectorAll('tbody tr:first-of-type td[ref ^= "datagrid"]').length;
                            componentTable.querySelector('tfoot td')?.setAttribute('colspan', cols.toString());

                            if (window.innerWidth > 650) {
                                componentTable.querySelectorAll('tbody > tr > td:not(:last-child)').forEach((x: HTMLTableDataCellElement) => {
                                    x.style.width = `calc(100% / ${cols})`;
                                });
                            } else {
                                const thElements = componentTable.querySelectorAll('thead th');
                                const tdLabels = Array.from(thElements).map((x: HTMLTableHeaderCellElement) => x.innerText.trim());
                                componentTable.querySelectorAll('tbody tr').forEach(tr => {
                                    Array.from(tr.children).forEach((td, i) => td.setAttribute('label', tdLabels[i]));
                                });
                            }
                        }
                    }
                }, 1000);
            }
        }, 500);
        // #endregion
    }

    saveDraft(event) {
        if (event.data == null) return;

        // se rota "/flow-instance"
        if (this.$flowObjectInstance() != null) return;

        let defaultFormData = window.localStorage.getItem(`${this.$flowDefinition().id}_default`);
        if (this.isFirstFormChange && defaultFormData == null) {
            // armazena o estado padrão do formulário para efeito de comparação posterior
            let compressed = compressToUTF16(JSON.stringify(event.data));
            window.localStorage.setItem(`${this.$flowDefinition().id}_default`, compressed);
            this.isFirstFormChange = false;
            return;
        }

        let tempData = JSON.parse(JSON.stringify(event.data));
        Utils.updateDeep(
            this.$formSchema(),
            x => ['pdfUpload', 'pdfUploadMultiple'].includes(x.type),
            x => delete tempData[x.key]
        );

        let compressed = compressToUTF16(JSON.stringify({
            data: tempData,
            version: this.$flowDefinition().version
        }));
        window.localStorage.setItem(this.$flowDefinition().id, compressed);
    }

    clearDraft() {
        window.localStorage.removeItem(this.$flowDefinition().id);
        this.toastr.success(Enums.Messages.FormDraftCleared, Enums.Messages.Form);
    }

    async loadFormData() {
        this.$isLocalLoading.set(true);
        this.spinner.show('local');

        try {
            const response = await this.flowObjectDefinitionService.getFormSchema(this.$model().id);

            if (!response.isSuccess) {
                this.toastr.error(response.message.description, Enums.Messages.Error, Utils.getToastrErrorOptions());
                return;
            }

            setTimeout(() => {
                this.$formSchema.set(JSON.parse(response.data));
                this.processFormSchema();
            }, 400);
        } finally {
            setTimeout(() => {
                this.spinner.hide('local');
                this.$isLocalLoading.set(false);
            }, 500);
        }
    }

    signerIdChange(event) {
        Utils.updSignal(this.$inputData, x => {
            x.eDocsData.signerId = event;
            return x;
        });
    }

    // #region [===== private methods =====]
    // #endregion

    private processFormSchema() {
        // #region [alteração do texto do botão "Enviar" do formulário]
        // para evitar alegações de usuários advogados dizendo que o PDF do preview é comprovante suficiente de envio do formulário
        Utils.updSignal(this.$formSchema, x => {
            let component = x.components.find(x => x.type == 'button' && x.action == 'submit');
            component.label = Enums.Messages.SendButtonText;
            component.rightIcon = 'fas fa-chevron-right';
            return x;
        });
        // #endregion

        // #region [ajuste de ícones da funcionalidade de "Dica" para Font Awesome 7]
        setInterval(() => {
            let components = Utils.filterDeep(this.$formSchema().components, x => Utils.isNotNullOrEmpty(x.tooltip));

            for (let item of components) {
                let icon = document.querySelector(`.formio-component-${item.key} > [ref=label] > [ref=tooltip]`);
                if (icon == null || icon.classList.contains('fa-question-circle')) continue;
                icon.classList.remove('fa');
                icon.classList.remove('fa-question-sign');
                icon.classList.add('fas');
                icon.classList.add('fa-question-circle');
            }
        }, 1000);
        // #endregion

        // #region [alteração on-the-fly do tipo base de componentes customizados por "extends" no FormBuilder]
        // para evitar erros de "Unknown component" durante a renderização
        Utils.updSignal(this.$formSchema, x => {
            this.processComponents(x);
            return x;
        });
        // #endregion

        // #region [ajuste na validação de campos do tipo "day"]
        Utils.updSignal(this.$formSchema, x => {
            let dayComponents = x.components.filter(x => x.type == 'day');

            for (let component of dayComponents) {
                component.customValidationSettings = {
                    minDate: component.minDate,
                    maxDate: component.maxDate,
                    minYear: component.minYear,
                    maxYear: component.maxYear
                };

                delete component.minDate;
                delete component.maxDate;
                delete component.minYear;
                delete component.maxYear;

                if (component.fields) {
                    if (component.fields.day) component.fields.day.required = false;
                    if (component.fields.month) component.fields.month.required = false;
                    if (component.fields.year) component.fields.year.required = false;
                }

                component.validate = component.validate || {};
                component.validate.required = false;

                component.validate.custom = `
                    const val = input || '';
                    const parts = val.split('/');
                    const isDayFirst = component.dayFirst === true;
                    const cfg = component.fields || {};
                    const isDayVisible = !cfg.day?.hide;
                    const isMonthVisible = !cfg.month?.hide;
                    const isYearVisible = !cfg.year?.hide;
                    const rules = component.customValidationSettings || {};
                    let rawDay = isDayVisible ? (isDayFirst ? parts[0] : parts[1]) : '';
                    let rawMonth = !isDayVisible ? parts[0] : isMonthVisible ? (isDayFirst ? parts[1] : parts[0]) : '';
                    let rawYear = isYearVisible ? parts.at(1) : '';
                    const clean = (str) => (str || '').trim().replace(/^0+/, '');
                    const dStr = clean(rawDay);
                    const mStr = clean(rawMonth);
                    const yStr = clean(rawYear);
                    if (isDayVisible && cfg.day?.required && !dStr) return valid = 'Dia é obrigatório.';
                    if (isMonthVisible && cfg.month?.required && !mStr) return valid = 'Mês é obrigatório.';
                    if (isYearVisible && cfg.year?.required && !yStr) return valid = 'Ano é obrigatório.';
                    const hasAnyValue = dStr || mStr || yStr;
                    if (!hasAnyValue) return valid = true;
                    const dayNum = parseInt(dStr, 10);
                    const monthNum = parseInt(mStr, 10);
                    const yearNum = parseInt(yStr, 10);
                    if (isDayVisible && dStr) {
                        if (isNaN(dayNum) || dayNum < 1 || dayNum > 31) return valid = 'Entre com um dia válido.';
                    }
                    if (isMonthVisible && mStr) {
                        if (isNaN(monthNum) || monthNum < 1 || monthNum > 12) return valid = 'Entre com um mês válido.';
                    }
                    if (isYearVisible && yStr) {
                        if (isNaN(yearNum)) return valid = 'Entre com um ano válido.';
                        if (rules.minYear && yearNum < parseInt(rules.minYear, 10)) return valid = 'Ano deve ser no mínimo ' + rules.minYear + '.';
                        if (rules.maxYear && yearNum > parseInt(rules.maxYear, 10)) return valid = 'Ano deve ser no máximo ' + rules.maxYear + '.';
                    }
                    const parseIso = (isoStr) => {
                        if (!isoStr) return null;
                        const d = new Date(isoStr);
                        return isNaN(d.getTime()) ? null : d;
                    };
                    const minBound = parseIso(rules.minDate);
                    const maxBound = parseIso(rules.maxDate);
                    if (minBound || maxBound) {
                        if (isDayVisible && isMonthVisible && isYearVisible) {
                            if (!isNaN(dayNum) && !isNaN(monthNum) && !isNaN(yearNum)) {
                                const targetDate = new Date(yearNum, monthNum - 1, dayNum);
                                if (minBound && targetDate < minBound) return valid = 'Data não pode ser menor que ' + minBound.toLocaleDateString() + '.';
                                if (maxBound && targetDate > maxBound) return valid = 'Data não pode ser maior que ' + maxBound.toLocaleDateString() + '.';
                            }
                        }
                        else if (!isDayVisible && isMonthVisible && isYearVisible) {
                            if (!isNaN(monthNum) && !isNaN(yearNum)) {
                                if (minBound) {
                                    if (yearNum < minBound.getFullYear() || (yearNum === minBound.getFullYear() && (monthNum - 1) < minBound.getMonth())) {
                                        return valid = 'Mês/Ano não pode ser menor que ' + (minBound.getMonth() + 1) + '/' + minBound.getFullYear() + '.';
                                    }
                                }
                                if (maxBound) {
                                    if (yearNum > maxBound.getFullYear() || (yearNum === maxBound.getFullYear() && (monthNum - 1) > maxBound.getMonth())) {
                                        return valid = 'Mês/Ano não pode ser maior que ' + (maxBound.getMonth() + 1) + '/' + maxBound.getFullYear() + '.';
                                    }
                                }
                            }
                        }
                        else if (!isDayVisible && !isMonthVisible && isYearVisible) {
                            if (!isNaN(yearNum)) {
                                if (minBound && yearNum < minBound.getFullYear()) return valid = 'Ano não pode ser menor que ' + minBound.getFullYear() + '.';
                                if (maxBound && yearNum > maxBound.getFullYear()) return valid = 'Ano não pode ser maior que ' + maxBound.getFullYear() + '.';
                            }
                        }
                    }
                    valid = true;
                `;
            }

            return x;
        });
        // #endregion
    }

    private processComponents(obj) {
        if (obj == null) return;

        if (Array.isArray(obj.components)) {
            obj.components.filter(x => x.type == 'datagridCustom').forEach(x => x.type = 'datagrid');
            obj.components.filter(x => x.type == 'tableCustom').forEach(x => x.type = 'table');

            obj.components = Utils.retypeCustomComponents(obj.components);

            for (let component of obj.components) {
                if (component == null) continue;

                if (Array.isArray(component.components)) {
                    this.processComponents(component);
                }

                if (Array.isArray(component.rows)) {
                    for (let row of component.rows) {
                        for (let column of row) {
                            this.processComponents(column);
                        }
                    }
                }
            }
        }
    }
}
